import { v4 as uuidV4 } from 'uuid';

import { TPartialSessionWithUuid, SessionTypesEnum, Session } from '@/types/session.types';
import getHostUrl from '@/utils/get-host-url';
import { BLAdmin } from '@/types/profile.types';
import { LanguagesAbbr } from '@/types/translation.types';

type FSDataParams = {
  session: TPartialSessionWithUuid;
  user: BLAdmin;
  projectDefaultLanguage?: LanguagesAbbr;
};

export const getRequiredSessionDataIfFireside = ({
  session,
  user,
}: FSDataParams): TPartialSessionWithUuid | { error: string } => {
  if (session.session_type !== SessionTypesEnum.fireside) {
    return session;
  }

  const hosts = session.firesides_hosts?.length ? session.firesides_hosts : [user.email];
  if (!hosts?.length) {
    return { error: 'At least one host email required' };
  }

  const updatedSession = { ...session };

  /*
    Fireside sessions can only have one language.
    So if updatedSession has more than one language we need remove all but one
  */
  if ((updatedSession?.languages || []).length > 1) {
    const newSessionDefault = updatedSession.default_language || updatedSession?.languages?.[0] || 'en';
    updatedSession.languages = [newSessionDefault];
    updatedSession.default_language = newSessionDefault;
  }

  // if no timestamp, add one that is 7 days into the future
  if (!updatedSession?.timestamp) {
    const startDate = new Date();
    startDate.setDate(startDate.getDate() + 7);
    startDate.setSeconds(0);
    startDate.setMilliseconds(0);
    updatedSession.timestamp = startDate.getTime();
    updatedSession.requires_time_change = {
      start_date: true, // ensure we require a start date change if we don't have a timestamp
      start_time: true, // ensure we require a start date change if we don't have a timestamp
      end_date: updatedSession.requires_time_change?.end_date ?? false,
      end_time: updatedSession.requires_time_change?.end_time ?? false,
    };
  }
  // if no end_timestamp, add one that is 7 days and 1 hour into the future
  if (!updatedSession?.end_timestamp) {
    const endDate = new Date();
    endDate.setDate(endDate.getDate() + 7);
    endDate.setSeconds(0);
    endDate.setMilliseconds(0);
    endDate.setHours(endDate.getHours() + 1); // one hour after start time
    updatedSession.end_timestamp = endDate.getTime();
    updatedSession.requires_time_change = {
      start_date: updatedSession.requires_time_change?.start_date ?? false,
      start_time: updatedSession.requires_time_change?.start_time ?? false,
      end_date: true, // ensure we require an end date change if we don't have an end_timestamp
      end_time: true, // ensure we require an end date change if we don't have an end_timestamp
    };
  }

  // if fireside, ensure we have the data we need to make it a google meet fireside
  if (!updatedSession?.fireside_session_settings) {
    updatedSession.fireside_session_settings = {
      uuid: uuidV4(),
    };
  }
  updatedSession.firesides_hosts = hosts;

  updatedSession.fireside_session_settings = {
    ...updatedSession.fireside_session_settings,
    settings: {
      ...updatedSession.fireside_session_settings?.settings,
      // Note: because we use google_meet_use_service_account, we do not need to pass in a google_meet_code
      // see getGoogleRefreshToken in api for more info
      google_meet_use_service_account: true,
      google_meet_site_url: getHostUrl(),
      use_google_meet: updatedSession.fireside_session_settings?.settings?.use_google_meet ?? true,
    },
  };

  return updatedSession;
};

type FSProjectLanguagesParams = {
  projectLanguages?: LanguagesAbbr[];
  isFireside: boolean;
  sessions: Session[];
  updatedSession: TPartialSessionWithUuid;
  projectDefaultLanguage?: string;
};

export const getUpdatedProjectLanguagesWhenConvertingToFireside = ({
  projectDefaultLanguage,
  projectLanguages,
  isFireside,
  sessions,
  updatedSession,
}: FSProjectLanguagesParams) => {
  if (!isFireside) {
    return projectLanguages || [];
  }

  // get all languages in every session, excluding the one we are updating
  const languagesInAllOtherSessions = sessions.flatMap((_session) => {
    if (_session.uuid === updatedSession.uuid) return [];
    return _session.languages;
  }) as LanguagesAbbr[];

  // Extremely unlikely we'll ever hit the fallback `en` but doing it for TS.
  // Plus, the user can always just updated it at any time after the project type conversion anyway. So no big deal.
  const newFSLanguages = (updatedSession.languages || [
    updatedSession.default_language || projectDefaultLanguage || 'en',
  ]) as LanguagesAbbr[];

  // new event_languages should be all languages from every other session plus the newly updated session languages
  const updatedProjectLanguages: LanguagesAbbr[] = [...languagesInAllOtherSessions, ...newFSLanguages];
  // then just remove duplicates
  const uniqueUpdatedProjectLanguages = Array.from(new Set(updatedProjectLanguages));

  // don't make any updates to event_languages if for some reason we don't get any languages (unlikely, but better safe than sorry)
  if (!uniqueUpdatedProjectLanguages.length) {
    return projectLanguages;
  }

  return uniqueUpdatedProjectLanguages;
};
